import { forwardRef, type InputHTMLAttributes, useEffect, useState } from "react";
import {
  buildCountryData,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import "react-international-phone/style.css";
import { getName, registerLocale } from "i18n-iso-countries";
import countryNames from "i18n-iso-countries/langs/pt.json";
import { InternationalPhoneSelectFlagDrawer } from "@/components/input-international-phone/international-phone-select-flag-drawer";
import { type SelectDrawerItemProps } from "@/components/select-drawer";

interface InputInternationalPhoneProps extends InputHTMLAttributes<HTMLInputElement> {
  title?: string;
  required?: boolean;
  placeholder?: string;
  helpMessage?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement> | string) => void;
  onCountryChange?: (country: string) => void;
}

registerLocale(countryNames);

export const InputInternationalPhone = forwardRef<
  HTMLInputElement,
  InputInternationalPhoneProps
>(
  (
    {
      title = "Telefone",
      required = false,
      helpMessage,
      onChange,
      onCountryChange,
      ...props
    }: InputInternationalPhoneProps,
    _,
  ): JSX.Element => {
    const defaultCountriesWithNewFormat = defaultCountries.map((country) => {
      const parsedCountry = parseCountry(country);
      if (parsedCountry.iso2 === "br") {
        parsedCountry.format = "(..) .....-....";
      }
      return buildCountryData(parsedCountry);
    });

    const [showDrawer, setShowDrawer] = useState<boolean | undefined>(undefined);
    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
      usePhoneInput({
        defaultCountry: "br",
        value:
          typeof props.value === "string" ? props.value : props.value?.toString() ?? "",
        countries: defaultCountriesWithNewFormat,
        onChange: (data) => onChange && onChange(data.phone),
      });

    useEffect(() => {
      if (showDrawer === false) {
        inputRef.current?.focus();
      }
    }, [showDrawer, inputRef]);
    useEffect(() => {
      onCountryChange && onCountryChange(country.iso2);
    }, [onCountryChange, country]);

    const handleCountrySelection = (value: string): void => {
      const selectedCountry = defaultCountries.find(
        (c) => parseCountry(c).dialCode === value,
      );
      if (selectedCountry) {
        setCountry(parseCountry(selectedCountry).iso2);
      }
    };

    const items: SelectDrawerItemProps<string>[] = defaultCountries.map(
      (countryValue) => {
        const parsedCountry = parseCountry(countryValue);
        const countryName = getName(parsedCountry.iso2.toUpperCase(), "pt");
        return {
          label: `${countryName} (+${parsedCountry.dialCode})`,
          value: parsedCountry.dialCode,
        };
      },
    );

    return (
      <div className="w-full">
        {title ? (
          <label htmlFor="phone-input" className="font-medium">
            {title}
            {required ? "*" : null}
          </label>
        ) : null}
        <div className="relative">
          <button
            data-testid="country-flag-button"
            type="button"
            aria-label="Selecionar país"
            className="absolute left-4 top-4 cursor-pointer"
            onClick={() => setShowDrawer(true)}
          >
            <FlagImage
              data-testid="country-flag-image"
              iso2={country.iso2}
              style={{ display: "flex" }}
            />
          </button>
          <input
            id="phone-input"
            className="pl-12 pr-4 rounded-md hover:border-primary-300 focus:border-primary-400 focus:ring-primary-200 flex w-full border-2 border-neutral-200 bg-white p-4 text-sm font-normal text-neutral-600 transition-all duration-200 placeholder:text-neutral-300 focus:outline-none focus:ring disabled:bg-neutral-50 disabled:text-neutral-300 disabled:placeholder:text-neutral-300 gap-1"
            placeholder={props.placeholder ?? title}
            type="tel"
            ref={inputRef}
            maxLength={20}
            onChange={handlePhoneValueChange}
            {...props}
            value={inputValue}
          />
        </div>
        <span className="text-xs text-neutral-500">{helpMessage}</span>
        <InternationalPhoneSelectFlagDrawer
          open={Boolean(showDrawer)}
          onOpenChange={setShowDrawer}
          value={country.dialCode}
          items={items}
          onChange={handleCountrySelection}
        />
      </div>
    );
  },
);

InputInternationalPhone.displayName = "InputInternationalPhone";
